import { useCallback, useMemo } from 'react';
import { CASE_PLAN_SECTION_STATUSES } from '../../constants/reentry-planning.constants';
import { isPendingSection } from '../../utils/case-plan-section.util';
import {
  collectOrphanedArchivedWidgets,
  filterActiveWidgets,
  resolveWidgetDestructiveAction,
  withWidgetDestructiveMenu,
  withWidgetMoveMenu,
} from '../../utils/case-plan-widget.util';
import { useCasePlanWidgetConfirmation } from './useCasePlanWidgetConfirmation';
import { useCasePlanWidgetMutations } from './useCasePlanWidgetMutations';

/**
 * Owns widget reorder, cross-section move, and Delete/Archive lifecycle. Composes the
 * confirmation dialog and the optimistic mutation handlers that `useCasePlanDetailsHook`
 * forwards to the section list.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: (sections: object[] | ((current: object[]) => object[])) => void,
 *   reloadDetail: () => void,
 *   plan: object | null,
 *   sectionApi: object,
 * }} params
 */
export const useCasePlanWidget = ({
  casePlanId,
  plan,
  sections,
  setSections,
  reloadDetail,
  sectionApi = {},
}) => {
  const { isSectionEditable = false, activeSectionTab } = sectionApi;

  const destructiveAction = useMemo(
    () => resolveWidgetDestructiveAction(plan?.status),
    [plan?.status],
  );

  const isArchivedSectionTab = activeSectionTab === CASE_PLAN_SECTION_STATUSES.ARCHIVED;
  const isWidgetEditable =
    isSectionEditable && !isArchivedSectionTab && destructiveAction !== null;

  const {
    moveWidget,
    removeWidget,
    openMoveToSectionModal,
    closeMoveToSectionModal,
    confirmMoveToSection,
    getMoveDestinations,
    moveToSectionModal,
  } = useCasePlanWidgetMutations({
    casePlanId,
    sections,
    setSections,
    reloadDetail,
  });

  const { requestWidgetRemoval, widgetConfirmation } =
    useCasePlanWidgetConfirmation(removeWidget);

  const enrichSectionWidgets = useCallback(
    (section) => {
      const isPending = isPendingSection(section);
      const activeWidgets = filterActiveWidgets(section.widgets);
      const withMove = withWidgetMoveMenu(activeWidgets, {
        enabled:
          !isPending &&
          getMoveDestinations(section.id).length > 0 &&
          Boolean(openMoveToSectionModal),
        onMoveToSection: (widgetId) => openMoveToSectionModal(widgetId, section.id),
      });

      return withWidgetDestructiveMenu(withMove, {
        enabled: isWidgetEditable && !isPending,
        action: destructiveAction,
        sectionId: section.id,
        onRequestRemoval: requestWidgetRemoval,
      });
    },
    [
      destructiveAction,
      getMoveDestinations,
      isWidgetEditable,
      openMoveToSectionModal,
      requestWidgetRemoval,
    ],
  );

  const orphanedArchivedWidgets = useMemo(
    () => collectOrphanedArchivedWidgets(sections),
    [sections],
  );

  return {
    onMoveWidget: moveWidget,
    onRequestMoveWidgetToSection: openMoveToSectionModal,
    getMoveDestinations,
    moveToSectionModal,
    onConfirmMoveToSection: confirmMoveToSection,
    onCloseMoveToSectionModal: closeMoveToSectionModal,
    enrichSectionWidgets,
    orphanedArchivedWidgets,
    widgetConfirmation,
  };
};
